import { Fragment, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ID } from '@proofroom/schema';
import { changes as changeApi, type BatchDecisionResult, type ResolvedChange } from '../api/endpoints';
import { keys } from '../api/queries';
import { Chip, EmptyState, timeAgo } from '../components/ui';
import { canDecide, useSession } from '../auth/session';
import { usePeople } from '../model/people';
import { wordDiff } from '../model/wordDiff';

export interface ChangesPanelProps {
  documentId: ID;
  changes: ResolvedChange[];
  storyLabel(storyId: ID): string;
  onJump(change: ResolvedChange): void;
}

/** "12 accepted, 1 already decided": the ok count under the decision's own name, then failures grouped by cause. */
function summarizeBatch(results: BatchDecisionResult[], decision: 'accepted' | 'rejected'): string {
  const ok = results.filter((r) => r.ok).length;
  const parts = [`${ok} ${decision}`];
  const causes = new Map<string, number>();
  const labels: Record<string, string> = { conflict: 'already decided', forbidden: 'not allowed', not_found: 'not found' };
  for (const r of results) {
    if (r.ok) continue;
    const label = labels[r.error ?? ''] ?? 'failed';
    causes.set(label, (causes.get(label) ?? 0) + 1);
  }
  for (const [label, count] of causes) parts.push(`${count} ${label}`);
  return parts.join(', ');
}

export function ChangesPanel({ documentId, changes, storyLabel, onJump }: ChangesPanelProps) {
  const [filter, setFilter] = useState<'all' | 'proposed' | 'decided'>('all');
  const [plain, setPlain] = useState(false);
  const [selected, setSelected] = useState<Set<ID>>(new Set());
  const [batchResult, setBatchResult] = useState<string | null>(null);
  const { user } = useSession();
  const qc = useQueryClient();
  const shown = changes.filter((c) => filter === 'all' || (filter === 'proposed' ? c.status === 'proposed' : c.status !== 'proposed'));
  const proposedIds = changes.filter((c) => c.status === 'proposed').map((c) => c.id);
  const allSelected = proposedIds.length > 0 && proposedIds.every((id) => selected.has(id));

  const toggleOne = (id: ID) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(proposedIds));

  const decideBatch = useMutation({
    mutationFn: ({ decision, note }: { decision: 'accepted' | 'rejected'; note?: string }) =>
      note ? changeApi.decideBatch(Array.from(selected), decision, note) : changeApi.decideBatch(Array.from(selected), decision),
    onSuccess: (outcome, { decision }) => {
      setBatchResult(summarizeBatch(outcome.results, decision));
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: keys.changes(documentId) });
    },
    // Selection is left as-is on a request failure (network error, 500): the Failed indicator
    // below shows it, and the caller can just press the button again rather than reselect.
  });
  const runBatch = (decision: 'accepted' | 'rejected') => {
    if (selected.size === 0) return;
    const plural = `${selected.size} change${selected.size === 1 ? '' : 's'}`;
    let note: string | undefined;
    if (decision === 'rejected') {
      // A rejection always needs a reason (decideChange requires one per change); collected once
      // here and applied to every change in the batch.
      const entered = window.prompt(`Reason for rejecting ${plural} (shown to the author)`);
      if (!entered?.trim()) return;
      note = entered.trim();
    }
    if (!window.confirm(`${decision === 'accepted' ? 'Accept' : 'Reject'} ${plural}?`)) return;
    setBatchResult(null);
    decideBatch.mutate({ decision, note });
  };

  return (
    <>
      <div className="filter-row">
        {(['all', 'proposed', 'decided'] as const).map((f) => <button key={f} className={`btn sm${filter === f ? ' on' : ' ghost'}`} onClick={() => setFilter(f)}>{f[0].toUpperCase() + f.slice(1)}</button>)}
        <button className="btn sm ghost" style={{ marginLeft: 'auto' }} onClick={() => setPlain((p) => !p)}>{plain ? 'Show diff' : 'Show plain'}</button>
      </div>
      {canDecide(user) && proposedIds.length > 0 && (
        <div className="filter-row">
          <label className="btn-row" style={{ gap: 6 }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all proposed" />
            Select all proposed
          </label>
          <button className="btn sm" disabled={selected.size === 0 || decideBatch.isPending} onClick={() => runBatch('accepted')}>Accept selected</button>
          <button className="btn sm ghost" disabled={selected.size === 0 || decideBatch.isPending} onClick={() => runBatch('rejected')}>Reject selected</button>
          {decideBatch.isError && <span className="error-text">Failed</span>}
        </div>
      )}
      {batchResult && <div className="note" style={{ margin: '0 12px 8px' }}>{batchResult}</div>}
      {shown.length === 0 && <EmptyState title="No changes" glyph="Δ">Edit a story and press Send to designer.</EmptyState>}
      {shown.map((c) => (
        <ChangeCard
          key={c.id} change={c} documentId={documentId} label={storyLabel(c.storyId)} onJump={() => onJump(c)} plain={plain}
          selectable={canDecide(user) && c.status === 'proposed'} selected={selected.has(c.id)} onToggleSelect={() => toggleOne(c.id)}
        />
      ))}
    </>
  );
}

function ChangeCard({ change, documentId, label, onJump, plain, selectable, selected, onToggleSelect }: {
  change: ResolvedChange; documentId: ID; label: string; onJump(): void; plain: boolean;
  selectable: boolean; selected: boolean; onToggleSelect(): void;
}) {
  const qc = useQueryClient();
  const { user } = useSession();
  const { nameOf } = usePeople();
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const done = () => qc.invalidateQueries({ queryKey: keys.changes(documentId) });
  const accept = useMutation({ mutationFn: () => changeApi.accept(change.id, note.trim() || undefined), onSuccess: () => void done() });
  const reject = useMutation({ mutationFn: () => changeApi.reject(change.id, note.trim()), onSuccess: () => { setRejecting(false); void done(); } });
  const withdraw = useMutation({ mutationFn: () => changeApi.withdraw(change.id), onSuccess: () => void done() });
  const isAuthor = !!user && change.authorId === user.id;
  return (
    <div className="change">
      <div className="head">
        {selectable && <input type="checkbox" checked={selected} onChange={onToggleSelect} aria-label={`Select change ${change.id}`} />}
        <Chip kind={change.status}>{change.status}</Chip>
        <span>{change.kind}</span>
        <button className="btn sm ghost" onClick={onJump}>{label}</button>
        <span className="who">{nameOf(change.author, change.authorId)} · {timeAgo(change.createdAt)}</span>
      </div>
      {change.status === 'accepted' && <div className="note">Accepted. Not yet written into InDesign: the plugin applies it when the designer&apos;s InDesign is open.</div>}
      {change.status === 'applied' && <div className="note">Written into the InDesign document.</div>}
      <div className="diff">
        {change.kind === 'insert' && <div className="muted small">New paragraph, after {change.afterParaId ? 'the one above it in the story' : 'the start of the story'}</div>}
        {plain ? (
          <>
            {change.before && <div className="before">{change.before}</div>}
            {change.after && <div className="after">{change.after}</div>}
          </>
        ) : (
          (change.before || change.after) && (
            <p className="inline-diff">
              {wordDiff(change.before ?? '', change.after ?? '').map((seg, i) => {
                if (seg.kind === 'removed') return <del key={i}>{seg.text}</del>;
                if (seg.kind === 'added') return <ins key={i}>{seg.text}</ins>;
                return <Fragment key={i}>{seg.text}</Fragment>;
              })}
            </p>
          )
        )}
        {change.kind === 'delete' && !change.after && <div className="muted small">This paragraph is removed.</div>}
      </div>
      {change.status === 'superseded' && (
        <div className="note">
          {change.decisionNote === 'withdrawn' ? `Withdrawn by ${nameOf(change.author, change.authorId)}.` : 'Superseded by a later send or an accepted change on this paragraph.'}
        </div>
      )}
      {change.status !== 'superseded' && change.decisionNote && <div className="note"><strong>{nameOf(change.decidedByUser, change.decidedBy)}:</strong> {change.decisionNote}</div>}
      {isAuthor && change.status === 'proposed' && (
        <div className="btn-row" style={{ marginTop: 8 }}>
          <button className="btn sm ghost" disabled={withdraw.isPending} onClick={() => { if (window.confirm('Withdraw this change?')) withdraw.mutate(); }}>Withdraw</button>
          {withdraw.isError && <span className="error-text">Failed</span>}
        </div>
      )}
      {canDecide(user) && change.status === 'proposed' && (
        <div style={{ marginTop: 8 }}>
          {rejecting ? (
            <div>
              <textarea className="input" placeholder="Why this is rejected (required)" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="btn-row" style={{ marginTop: 6 }}>
                <button className="btn sm danger" disabled={!note.trim() || reject.isPending} onClick={() => reject.mutate()}>Reject</button>
                <button className="btn sm ghost" onClick={() => setRejecting(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="btn-row">
              <button className="btn sm" disabled={accept.isPending} onClick={() => accept.mutate()} title="Accept this change; the plugin writes it into InDesign">Accept</button>
              <button className="btn sm ghost" onClick={() => setRejecting(true)}>Reject</button>
              {(accept.isError || reject.isError) && <span className="error-text">Failed</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
